import React, { useState, useRef, useEffect } from "react";
import api from "../api/client";
import LoadingButton from "./LoadingButton";

const parseInlineMarkdown = (text) => {
  if (!text) return "";
  const regex = /(\*\*|__)(.*?)\1|(\*|_)(.*?)\3|(`)(.*?)\5/g;
  const elements = [];
  let lastIdx = 0;
  let match;
  let keyCount = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      elements.push(text.substring(lastIdx, match.index));
    }
    if (match[2]) {
      elements.push(
        <strong key={keyCount++} style={{ fontWeight: 700, color: "#f8fafc" }}>
          {match[2]}
        </strong>
      );
    } else if (match[4]) {
      elements.push(
        <em key={keyCount++} style={{ fontStyle: "italic" }}>
          {match[4]}
        </em>
      );
    } else if (match[6]) {
      elements.push(
        <code
          key={keyCount++}
          style={{
            background: "rgba(255, 255, 255, 0.15)",
            padding: "2px 5px",
            borderRadius: "4px",
            fontSize: "12px",
            fontFamily: "monospace",
          }}
        >
          {match[6]}
        </code>
      );
    }
    lastIdx = regex.lastIndex;
  }

  if (lastIdx < text.length) {
    elements.push(text.substring(lastIdx));
  }

  return elements.length > 0 ? elements : text;
};

const FormattedMessage = ({ text }) => {
  if (!text) return null;

  const lines = String(text).split("\n");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {lines.map((line, lineIdx) => {
        const trimmed = line.trim();

        if (!trimmed) {
          return <div key={lineIdx} style={{ height: "4px" }} />;
        }

        const bulletMatch = trimmed.match(/^(?:\*|-|\+|•)\s+(.*)/);
        if (bulletMatch) {
          return (
            <div
              key={lineIdx}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                paddingLeft: "4px",
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: "#60a5fa", fontSize: "14px", lineHeight: "1.4" }}>•</span>
              <span style={{ flex: 1 }}>{parseInlineMarkdown(bulletMatch[1])}</span>
            </div>
          );
        }

        const numMatch = trimmed.match(/^(\d+\.)\s+(.*)/);
        if (numMatch) {
          return (
            <div
              key={lineIdx}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                paddingLeft: "2px",
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: "#60a5fa", fontWeight: "600" }}>{numMatch[1]}</span>
              <span style={{ flex: 1 }}>{parseInlineMarkdown(numMatch[2])}</span>
            </div>
          );
        }

        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)/);
        if (headingMatch) {
          return (
            <div
              key={lineIdx}
              style={{
                fontWeight: 700,
                fontSize: "15px",
                color: "#93c5fd",
                marginTop: "4px",
                marginBottom: "2px",
              }}
            >
              {parseInlineMarkdown(headingMatch[2])}
            </div>
          );
        }

        return (
          <div key={lineIdx} style={{ lineHeight: 1.5 }}>
            {parseInlineMarkdown(line)}
          </div>
        );
      })}
    </div>
  );
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem("chatMessages");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    try {
      sessionStorage.setItem("chatMessages", JSON.stringify(messages));
    } catch (e) {
      console.error("Failed to save chatMessages to sessionStorage", e);
    }
  }, [messages]);

  const clearChat = () => {
    setMessages([]);
    sessionStorage.removeItem("chatMessages");
  };

  const sendMessage = async () => {
    if (!message.trim() || sending) return;
    setSending(true);

    const userMsg = { text: message, sender: "user" };
    const historySlice = [...messages, userMsg].slice(-30);
    setMessages((prev) => [...prev, userMsg]);

    try {
      const res = await api.post(`/chatbot`, {
        query: message,
        history: historySlice,
      });

      const botMsg = { text: res.data, sender: "bot" };
      setMessages((prev) => [...prev, botMsg]);
      setSending(false);
    } catch (err) {
      console.error("CHATBOT ERROR:", err);

      const backendMessage =
        typeof err.response?.data === "string"
          ? "Request failed. Please try again."
          : err.response?.data?.message || err.response?.data?.error;

      const errorMsg = {
        text: backendMessage || "Something went wrong",
        sender: "bot",
      };

      setMessages((prev) => [...prev, errorMsg]);
      setSending(false);
    }

    setMessage("");
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Open HR Assistant"
        title="HR Assistant"
        className="chatbot-fab"
      >
        💬
      </button>
    );
  }

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  return (
    <div
      style={{
        position: "fixed",
        right: isMobile ? "10px" : "20px",
        bottom: isMobile ? "10px" : "94px",
        width: isMobile ? "calc(100vw - 20px)" : "380px",
        height: isMobile ? "70vh" : "520px",
        maxHeight: "80vh",
        background: "#0b1223",
        border: "1px solid #1e3a8a",
        borderRadius: "16px",
        boxShadow: "0 24px 50px rgba(15, 23, 42, 0.55)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: "var(--z-chatbot-panel)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #1e3a8a, #1d4ed8)",
          color: "#ffffff",
          padding: "12px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "16px" }}>HR Assistant</span>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={clearChat}
            aria-label="Clear chat"
            title="Clear chat"
            style={{
              background: "#f59e0b",
              border: "none",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            🗑
          </button>

          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
            title="Close chat"
            style={{
              background: "#ef4444",
              border: "none",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          padding: "14px 14px 10px",
          overflowY: "auto",
          background: "#020617",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              background: "#1e293b",
              color: "#cbd5e1",
              borderRadius: "12px",
              padding: "10px 12px",
              fontSize: "13px",
              lineHeight: 1.5,
              maxWidth: "86%",
            }}
          >
            Ask about candidates, offers, or interview metrics.
          </div>
        )}

        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
              background: msg.sender === "user" ? "#2563eb" : "#334155",
              color: "#ffffff",
              padding: "10px 12px",
              borderRadius: "12px",
              maxWidth: "84%",
              fontSize: "14px",
              whiteSpace: "pre-wrap",
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            <FormattedMessage text={msg.text} />
          </div>
        ))}

        <div ref={bottomRef} />
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          padding: "10px",
          background: "#0f172a",
          borderTop: "1px solid #1f2a44",
        }}
      >
        <input
          type="text"
          placeholder="Ask candidate data..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") sendMessage();
          }}
          style={{
            flex: 1,
            height: "40px",
            borderRadius: "10px",
            border: "1px solid #334155",
            background: "#ffffff",
            color: "#0f172a",
            padding: "0 12px",
            outline: "none",
            fontSize: "14px",
          }}
        />

        <LoadingButton
          onClick={sendMessage}
          disabled={sending}
          loading={sending}
          loadingText="..."
          style={{
            background: sending ? "#1e40af" : "#2563eb",
            border: "none",
            color: "#fff",
            height: "40px",
            minWidth: "74px",
            borderRadius: "10px",
            fontWeight: 700,
            cursor: sending ? "not-allowed" : "pointer",
            opacity: sending ? 0.7 : 1,
          }}
        >
          Send
        </LoadingButton>
      </div>
    </div>
  );
};

export default Chatbot;
